import { API_BASE, getApiHeaders } from './apiClient';

export interface Attachment {
  filename:     string;
  originalName: string;
  url:          string;
  size:         number;
  ext:          string;
  isImage:      boolean;
  uploadedAt:   string;
}

export async function listAttachments(incidentId: string): Promise<Attachment[]> {
  const res  = await fetch(`${API_BASE}/cima/incidents/${incidentId}/attachments`, { headers: getApiHeaders() });
  const data = await res.json();
  return data.files ?? [];
}

export async function uploadAttachments(incidentId: string, files: File[]): Promise<Attachment[]> {
  const uploaded: Attachment[] = [];

  // Try presigned URL approach first (production)
  for (const file of files) {
    try {
      // Get presigned URL from backend
      const presignRes = await fetch(`${API_BASE}/cima/incidents/${incidentId}/attachments/presign`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ filename: file.name, contentType: file.type }),
      });

      if (presignRes.ok) {
        // Backend returns { uploadUrl, publicUrl, key, filename } — `url` /
        // `fileUrl` are kept as deprecated aliases. Reading uploadUrl/publicUrl
        // matches the training video + thumbnail upload clients.
        const presigned = await presignRes.json();
        const uploadUrl: string = presigned.uploadUrl ?? presigned.url;
        const publicUrl: string = presigned.publicUrl ?? presigned.fileUrl ?? `https://cima.enigmaglobal.ai/${presigned.key}`;
        const filename: string = presigned.filename;

        // Upload directly to S3 using presigned URL
        const s3Res = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });

        if (s3Res.ok) {
          const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
          uploaded.push({
            filename,
            originalName: file.name,
            url: publicUrl,
            size: file.size,
            ext,
            isImage: /^(jpe?g|png|gif|webp)$/.test(ext),
            uploadedAt: new Date().toISOString(),
          });
          continue;
        }
      }
    } catch {
      // Presign failed — fall through to multipart upload
    }

    // Fallback: direct multipart upload (local dev)
    const form = new FormData();
    form.append('files', file);
    const res = await fetch(`${API_BASE}/cima/incidents/${incidentId}/attachments`, {
      method: 'POST',
      body: form,
    });
    if (res.ok) {
      const data = await res.json();
      uploaded.push(...(data.files ?? []));
    }
  }

  return uploaded;
}

export async function deleteAttachment(incidentId: string, filename: string): Promise<void> {
  await fetch(`${API_BASE}/cima/incidents/${incidentId}/attachments/${filename}`, {
    method: 'DELETE',
    headers: getApiHeaders(),
  });
}
